// src/features/dashboard/useDashboardPrefetch.ts
import { useEffect, useMemo } from 'react'
import { useLocation, matchPath } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { prefetchAgentTrends } from '../services/api/agentService'
import { prefetchVolumeTrends } from '../services/api/trendService'

type Opts = {
  enabled: boolean
  days?: number
  whenPaths?: string[]
  token?: string // for later if we swap to Clerk JWT
}

function lastNDaysRange(n: number) {
  const end = new Date()
  const start = new Date()
  start.setDate(end.getDate() - (n - 1))
  const fmt = (d: Date) => d.toISOString().slice(0, 10)
  return { start_date: fmt(start), end_date: fmt(end) }
}

export function useDashboardPrefetch({
  enabled,
  days = 30,
  whenPaths = ['/', '/dashboard'],
  token
}: Opts) {
  const qc = useQueryClient()
  const loc = useLocation()

  const shouldRun = useMemo(() => {
    return (
      enabled &&
      whenPaths.some(p => matchPath({ path: p, end: true }, loc.pathname))
    )
  }, [enabled, whenPaths, loc.pathname])

  const range = useMemo(() => lastNDaysRange(days), [days])

  useEffect(() => {
    if (!shouldRun) return
    const volReq = { ...range, verbose: false, echo_result_columns: true }
    const agentReq = {
      ...range,
      contact_name: '',
      verbose: false,
      echo_result_columns: true
    }

    prefetchVolumeTrends(qc, volReq, token)
    prefetchAgentTrends(qc, agentReq, token)
  }, [shouldRun, range, qc, token])
}
